import { Gateway, ResponseType } from "./Gateway";
import { Method, BASE_URL, DEV_BASE_URL } from "../Config";
import { isDev } from "../../components/common/Common";

/**
 * AI 요약 요청 데이터 인터페이스
 */
export interface SummaryReqBody {
    mapData: any[][];
    dataCount: number;
    fields: string[];
    type: 'summary' | 'description';
}

/**
 * SSE 스트림 콜백 인터페이스
 */
export interface SSEStreamCallback {
    onChunk: (chunk: string) => void;
    onComplete: () => void;
    onError: (error: string) => void;
}

/**
 * AI 서비스 Gateway 클래스
 *
 * @author Kim Young Jun
 * @date 2025-09-19
 */
export class AIGateway extends Gateway {
    protected _contextPath: string = "ai";
    protected _pathParams?: { [key: string]: string } = {};
    protected _processParams?: { [key: string]: string } = {
        summary: "summary"
    };

    private typingQueue: string[] = [];
    private isTyping: boolean = false;
    private isStreamCompleted: boolean = false;
    private abortController: AbortController | null = null;
    private typingTimeoutId: number | null = null;
    private hasStartedTyping: boolean = false;
    private initialDelayTimeout: number | null = null;

    /**
     * 스트림 중단 (인스턴스 정리용)
     */
    public abortStream(): void {
        try {
            if (this.abortController) {
                this.abortController.abort();
                this.abortController = null;
            }
            this.stopTyping();
            this.resetState();
        } catch (error) {
            // abort 과정에서 발생하는 에러는 무시
            console.log('AbortController 정리 완료');
        }
    }

    /**
     * 타이핑 중단 및 타임아웃 정리
     */
    private stopTyping(): void {
        this.isTyping = false;
        if (this.typingTimeoutId) {
            clearTimeout(this.typingTimeoutId);
            this.typingTimeoutId = null;
        }
        if (this.initialDelayTimeout) {
            clearTimeout(this.initialDelayTimeout);
            this.initialDelayTimeout = null;
        }
    }

    /**
     * 상태 초기화
     */
    private resetState(): void {
        this.typingQueue = [];
        this.isTyping = false;
        this.isStreamCompleted = false;
        this.hasStartedTyping = false;
        this.stopTyping();
    }

    /**
     * AI 요약 스트림 요청
     *
     * @param body 요약 요청 데이터
     * @param callback SSE 스트림 콜백
     * @returns Promise<void>
     */
    public async reqSummaryStream(body: SummaryReqBody, callback: SSEStreamCallback): Promise<void> {
        console.log('AIGateway.reqSummaryStream 시작');

        // 완전히 새로운 상태로 시작
        this.resetState();

        // 새로운 AbortController 생성
        this.abortController = new AbortController();

        console.log('새로운 AbortController 생성 완료');
        try {
            const queryParam = {
                process: this._processParams!.summary,
                _t: Date.now().toString() // 캐시 버스팅용 타임스탬프
            };

            // 올바른 URL 생성 (Config의 BASE_URL/DEV_BASE_URL 사용)
            let url = `${isDev() ? DEV_BASE_URL : BASE_URL}/${this._contextPath}`;
            url = queryParam ? `${url}?${new URLSearchParams(queryParam).toString()}` : url;

            console.log('Fetch 요청 URL:', url);
            console.log('AbortController signal:', this.abortController.signal);

            // 연결 상태 안정화를 위한 짧은 대기
            await new Promise(resolve => setTimeout(resolve, 50));

            // POST 요청으로 SSE 연결 (캐시 방지 헤더 추가)
            const response = await fetch(url, {
                method: Method.POST,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                },
                body: JSON.stringify(body),
                credentials: 'include',
                signal: this.abortController.signal
            });

            console.log('Fetch 응답 받음:', response.status);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            if (!response.body) {
                throw new Error('Response body is null');
            }

            // ReadableStream 처리 - 개별 글자 처리를 위한 수정
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();

                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // 마지막 불완전한 줄은 버퍼에 보관

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const data = line.slice(6); // 'data: ' 제거

                        if (data === '[DONE]') {
                            this.isStreamCompleted = true;
                            // 큐가 비어있으면 바로 완료, 아니면 타이핑 완료 후 처리
                            if (this.typingQueue.length === 0 && !this.isTyping) {
                                this.resetState();
                                callback.onComplete();
                            }
                            return;
                        } else if (data.startsWith('ERROR:')) {
                            callback.onError(data.slice(6));
                            return;
                        } else if (data.trim() || data.includes(' ') || data.includes('\\n') || data.includes('\\t')) {
                            // 이스케이프 시퀀스를 실제 문자로 변환
                            let processedData = data;
                            processedData = processedData.replace(/\\n/g, '\n'); // \\n을 실제 개행 문자로
                            processedData = processedData.replace(/\\t/g, '\t'); // \\t을 실제 탭 문자로

                            // 변환된 데이터를 글자별로 큐에 추가
                            const chars = Array.from(processedData);

                            // 디버깅: 받은 문자들 로그
                            // chars.forEach(char => {
                            //     if (char === '\n') {
                            //         console.log('AIGateway: 이스케이프 시퀀스 \\\\n을 개행문자로 변환');
                            //     } else if (char === '\t') {
                            //         console.log('AIGateway: 이스케이프 시퀀스 \\\\t을 탭문자로 변환');
                            //     } else if (char === ' ') {
                            //         console.log('AIGateway received: 공백문자');
                            //     } else {
                            //         console.log(`AIGateway received: '${char}'`);
                            //     }
                            // });

                            this.typingQueue.push(...chars);

                            // 처음 데이터가 오면 0.3초 대기 후 타이핑 시작
                            if (!this.hasStartedTyping) {
                                this.hasStartedTyping = true;
                                this.isTyping = true; // 타이핑 상태로 설정
                                this.initialDelayTimeout = window.setTimeout(() => {
                                    this.processTypingQueue(callback);
                                }, 300);
                            }
                        }
                    }
                }
            }

        } catch (error) {
            console.log('AIGateway 요청 중 에러:', error);
            if (error instanceof Error && error.name === 'AbortError') {
                console.log('사용자가 요청을 중단했습니다.');
                return;
            }
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.log('에러 메시지:', errorMessage);
            this.resetState();
            callback.onError(errorMessage);
        }
    }

    /**
     * 타이핑 큐 처리 (한 글자씩 순차 전송)
     */
    private processTypingQueue(callback: SSEStreamCallback): void {
        // 중단되었으면 종료 (사용자가 pause 누른 경우)
        if (!this.isTyping) {
            return;
        }

        // 큐가 비어있으면 대기 상태
        if (this.typingQueue.length === 0) {
            if (this.typingTimeoutId) {
                clearTimeout(this.typingTimeoutId);
                this.typingTimeoutId = null;
            }
            // 스트림이 완료되었고 큐도 비었으면 완료 콜백 호출
            if (this.isStreamCompleted) {
                this.isTyping = false;
                this.resetState();
                callback.onComplete();
            } else {
                // 스트림이 진행 중이면 잠시 후 다시 확인
                this.typingTimeoutId = window.setTimeout(() => {
                    this.processTypingQueue(callback);
                }, 50);
            }
            return;
        }

        const char = this.typingQueue.shift()!;
        callback.onChunk(char);

        this.typingTimeoutId = window.setTimeout(() => {
            this.processTypingQueue(callback);
        }, 20);
    }

    /**
     * AI 요약 요청 (일반 방식 - 향후 사용)
     *
     * @param body 요약 요청 데이터
     * @returns Promise<ResponseType>
     */
    public async reqSummary(body: SummaryReqBody): Promise<ResponseType> {
        const queryParam = { process: this._processParams!.summary };
        return await this.request(Method.POST, undefined, queryParam, body);
    }

}